import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { Box, Tooltip } from '@mui/material'

import { Icon, IconButton } from '~components/internal'
import { FEATURE_FLAGS } from '~featureFlags'
import { useFeatureGate } from '~hooks/access/useFeatureGate'
import { type RootState } from '~redux/rootReducer'

import './ActiveCalls.scss'
import ActiveCallsDialog from './ActiveCallsDialog'
import { useActiveCallCount } from './useActiveCallCount'

const MAX_BADGE_COUNT = 99

const ActiveCallsButton = (): React.ReactElement | null => {
  const { enabled } = useFeatureGate(FEATURE_FLAGS.ENABLE_WARM_HANDOFF_ON_TRANSFER)
  const organizationIdentityId = useSelector(
    (state: RootState) => state?.organization.selectedOrganization?.id ?? null,
  )
  const userId = useSelector((state: RootState) => state?.auth.userId ?? null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const { activeCallCount, updateToken } = useActiveCallCount(organizationIdentityId, enabled)

  if (!enabled || !organizationIdentityId) return null

  const isActive = activeCallCount > 0
  const tooltip = isActive
    ? `${activeCallCount} active AI ${activeCallCount === 1 ? 'call' : 'calls'} in progress`
    : 'Active AI calls: shows the number of calls currently in progress'

  return (
    <>
      <Tooltip title={tooltip}>
        <Box
          component='span'
          className={`active-calls-button${isActive ? ' active-calls-button--active' : ''}`}
        >
          <IconButton aria-label={tooltip} onClick={() => setDialogOpen(true)}>
            <Icon name='call' fontSize='small' />
          </IconButton>
          {isActive && (
            <Box component='span' className='active-calls-button__badge' aria-hidden>
              {activeCallCount > MAX_BADGE_COUNT ? `${MAX_BADGE_COUNT}+` : activeCallCount}
            </Box>
          )}
        </Box>
      </Tooltip>
      <ActiveCallsDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        organizationIdentityId={organizationIdentityId}
        userId={userId}
        updateToken={updateToken}
      />
    </>
  )
}

export default ActiveCallsButton
